import { ADICIONANDO, contasDisponiveis, guardarContaAtual, listarContas } from '../contas/contasDoAparelho';
import { podeGuardar } from '../contas/regrasDasContas';
import { esquecerContaDaSala, lembrarContaDaSala } from '@/features/aplicativo/contaDaSala';
import { desligarBiometria, OFERECER_AGORA } from '../biometria/entradaBiometrica';
import { desligarTrava } from '../biometria/regrasDaTrava';
import { removerAparelhoDoPush } from '@/features/notificacoes/pushDoAparelho';
import React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/utils/api";
import { LoginPayload } from "../types/LoginPayload";
import { UserData } from "../types/UserData";

interface AuthContextType {
  user: UserData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  /** A sessão devolvida por outro caminho de entrada — o Google, a biometria, o "Trocar de conta". */
  entrarComSessao: (sessao: { token: string; user: UserData }) => void;
  logout: () => void;
  /** "Adicionar outra conta" (1.4): fecha a sessão SEM apagar as contas guardadas, e abre o login no modo "adicionar". */
  sairParaAdicionarConta: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

/** Decodifica payload do JWT sem verificar assinatura (client-side). */
function decodeJwtPayload(token: string): { exp?: number; [key: string]: any } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload;
  } catch {
    return null;
  }
}

/** Verifica se o token está expirado. */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return Date.now() / 1000 > payload.exp;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Tenta fazer refresh do token
  const tryRefresh = useCallback(async (token: string): Promise<boolean> => {
    try {
      const response = await api.post("/api/auth/refresh", {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { token: newToken, user: newUser } = response.data;
      localStorage.setItem("token", newToken);
      localStorage.setItem("user", JSON.stringify(newUser));
      setUser(newUser);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Inicialização: verificar token armazenado
  useEffect(() => {
    const init = async () => {
      const storedUser = localStorage.getItem("user");
      const storedToken = localStorage.getItem("token");

      if (!storedUser || !storedToken || storedUser === "undefined") {
        setIsLoading(false);
        return;
      }

      if (!isTokenExpired(storedToken)) {
        // Token válido
        try {
          const u = JSON.parse(storedUser);
          setUser(u);
          // A conta da sala (02/11/2026): quem já estava logado também fica guardado no aparelho (`contaDaSala.ts`).
          void lembrarContaDaSala(u);
        } catch {
          setUser(null);
        }
      } else {
        // Token expirado — tentar refresh
        const refreshed = await tryRefresh(storedToken);
        if (!refreshed) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      }
      setIsLoading(false);
    };
    init();
  }, [tryRefresh]);

  // Escutar evento auth:logout (disparado pelo interceptor axios)
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      navigate("/login");
    };
    window.addEventListener("auth:logout", handleLogout);
    return () => window.removeEventListener("auth:logout", handleLogout);
  }, [navigate]);

  // Escutar evento auth:refreshed (token renovado pelo interceptor)
  useEffect(() => {
    const handleRefreshed = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.user) {
        setUser(detail.user);
      }
    };
    window.addEventListener("auth:refreshed", handleRefreshed);
    return () => window.removeEventListener("auth:refreshed", handleRefreshed);
  }, []);

  // O servidor recusou com SENHA_TROCA_OBRIGATORIA (achado 204). Acontece com a sessão
  // aberta ANTES da marca: o usuário guardado no aparelho não a tem.
  useEffect(() => {
    const handleTroca = () => {
      // Já marcado: o MESMO objeto. Um novo a cada 403 refazia os efeitos que dependem de `user`, que chamavam a rota de
      // novo, que dava 403 — o laço do achado D16 (22/10/2026).
      setUser((u) => {
        if (!u || u.trocaDeSenhaObrigatoria) return u;
        const marcado = { ...u, trocaDeSenhaObrigatoria: true };
        localStorage.setItem("user", JSON.stringify(marcado));
        return marcado;
      });
      if (window.location.pathname !== "/trocar-senha") navigate("/trocar-senha", { replace: true });
    };
    window.addEventListener("auth:trocaDeSenha", handleTroca);
    return () => window.removeEventListener("auth:trocaDeSenha", handleTroca);
  }, [navigate]);

  // O servidor recusou com TERMOS_PENDENTES (pedido de acesso, commit 4): a conta criada por
  // pedido ainda não aceitou os termos de uso. A saída é a tela do aceite.
  useEffect(() => {
    const handleTermos = () => {
      setUser((u) => {
        if (!u || u.termosPendentes) return u;
        const marcado = { ...u, termosPendentes: true };
        localStorage.setItem("user", JSON.stringify(marcado));
        return marcado;
      });
      if (window.location.pathname !== "/termos/aceitar") navigate("/termos/aceitar", { replace: true });
    };
    window.addEventListener("auth:termos", handleTermos);
    return () => window.removeEventListener("auth:termos", handleTermos);
  }, [navigate]);

  // Refresh periódico a cada 1h
  useEffect(() => {
    if (user) {
      refreshIntervalRef.current = setInterval(async () => {
        const token = localStorage.getItem("token");
        if (token) {
          await tryRefresh(token);
        }
      }, 60 * 60 * 1000); // 1 hora
    } else {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [user, tryRefresh]);

  // Um só lugar grava a sessão: a senha e o Google recebem do servidor a MESMA
  // resposta (`montarSessao`), e duas gravações divergiriam no primeiro campo novo.
  const entrarComSessao = useCallback(({ token, user }: { token: string; user: UserData }) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    setUser(user);
    // A conta da sala sem internet (02/11/2026): fica guardada no aparelho até o "Sair" de propósito (`contaDaSala.ts`).
    void lembrarContaDaSala(user as never);
    // "Adicionar outra conta": a conta que acabou de entrar (com a senha ou o Google) é guardada neste aparelho. Aluno não é
    // guardado (`podeGuardar`); a falha fica anotada para Minha conta dizer por quê.
    let adicionando = false;
    try { adicionando = sessionStorage.getItem(ADICIONANDO) === '1'; sessionStorage.removeItem(ADICIONANDO); } catch { /* sem a marca */ }
    // No navegador nada é guardado sozinho: só pela caixa "Lembrar esta conta neste navegador" (`LoginPage`).
    /*
     * E A CONTA DA EQUIPE QUE ENTRA PELA TELA DE LOGIN, NUM APARELHO QUE JÁ USA O "TROCAR DE CONTA" (02/11/2026, o defeito
     * do iPhone 1.5/82): até aqui a conta só entrava na lista pela folha (aberta DENTRO dela) ou pelo "Adicionar outra
     * conta". A que tinha saído da lista — e a que entrou pelo Google, ou pela senha, sem passar por ali — ficava de fora,
     * e voltar a ela era o Google (ou a senha) de novo. Agora entra na lista sozinha, se a lista já tem alguém: quem nunca
     * usou a troca neste aparelho continua sem nada guardado (num celular alheio, a biometria do dono abriria a conta).
     * A sessão aberta pelo cofre (`entrar-com-aparelho`) já está na lista, e o servidor recusa guardar por ela.
     */
    if (!adicionando && podeGuardar(user) && contasDisponiveis()) {
      void listarContas().then((lista) => {
        if (lista.length > 0 && !lista.some((c) => c.userId === Number(user.id))) {
          return guardarContaAtual(user).catch((e) => { try { sessionStorage.setItem('contas:erro', (e as Error).message); } catch { /* nada */ } });
        }
      });
    }
    if (adicionando && podeGuardar(user) && contasDisponiveis()) {
      void guardarContaAtual(user).catch((e) => { try { sessionStorage.setItem('contas:erro', (e as Error).message); } catch { /* nada */ } });
    }
    // A troca de senha obrigatória vem antes de tudo; depois, a sugestão do Google.
    navigate(user?.trocaDeSenhaObrigatoria ? "/trocar-senha" : user?.termosPendentes ? "/termos/aceitar" : user?.sugerirGoogle ? "/vincular-google" : "/dashboard");
  }, [navigate]);

  const login = useCallback(async (payload: LoginPayload) => {
    const response = await api.post('/api/auth/login', payload);
    // 1.4: entrou COM SENHA — o aplicativo pode oferecer a biometria (`biometria/OfertaDeBiometria`).
    try { sessionStorage.setItem(OFERECER_AGORA, '1'); } catch { /* sem a oferta desta vez */ }
    entrarComSessao(response.data);
  }, [entrarComSessao]);

  const logout = useCallback(() => {
    // 1.4: sair da conta apaga também o acesso guardado no cofre do aparelho.
    void desligarBiometria();
    // "Sair" SAI, e a conta continua lembrada (24/10/2026, revisão do pesquisador): no navegador e no app. Tirar a conta da
    // lista é "Esquecer esta conta", em cada conta da folha e do login; tirar todas, "Sair de todas as contas deste navegador".
    void desligarTrava(user?.id); // e a trava ao abrir
    void removerAparelhoDoPush(); // e o aparelho deixa de receber push desta conta
    // E a conta da sala sai do aparelho: as salas dela ficam travadas (02/11/2026). Só ESTE "Sair" — a sessão vencida
    // (o `auth:logout` do interceptador) não passa por aqui, e a sala preparada continua abrindo sem internet.
    void esquecerContaDaSala(user?.id);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login");
  }, [navigate, user?.id]);

  const sairParaAdicionarConta = useCallback(() => {
    try { sessionStorage.setItem(ADICIONANDO, '1'); } catch { /* sem a marca, a conta nova não é guardada sozinha */ }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login?adicionar=1");
  }, [navigate]);

  const value = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    entrarComSessao,
    logout,
    sairParaAdicionarConta
  }), [user, isLoading, login, entrarComSessao, logout, sairParaAdicionarConta]);

  return React.createElement(
    AuthContext.Provider,
    { value },
    children
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider };
